import { memo, useCallback, useMemo } from 'react';

import { useCaptureException } from '@zenbusiness/application-commons-logging';
import { WebView } from '@zenbusiness/application-commons-webview';
import { useTheme } from '@zenbusiness/zui/styles';
import PropTypes from 'prop-types';

import { COUNTERPARTY_CONNECTED_TYPE } from '#client/constants/counterParty';
import useConstBusinessEntityUuid from '#client/hooks/useConstBusinessEntityUuid';
import usePlaid from '#client/hooks/usePlaid';
import useRedirectToLinkedAccounts from '#client/hooks/useRedirectToLinkedAccounts';
import useToastContext from '#client/hooks/useToastContext';
import useUnitAccountId from '#client/hooks/useUnitAccountId';
import { getRelinkActionLabel, isNotRelinkableStatus } from '#client/utils/counterpartyStatus';
import { retrievedGraphQLError } from '#client/utils/errors';
import { relinkPlaidWithNativeApp } from '#client/utils/Plaid/getPlaidWebview';
import { capitalizeFirstLetter } from '#client/utils/text';

import AccountCommon from './AccountCommon';
import AccountCommonV2 from './AccountCommonV2';

function RelinkAccount({
  id,
  name,
  bankName,
  connectedType,
  accountType,
  last4Digits,
  numberOfCharacters = 2,
  resetPagination,
  setShowSkeleton,
  setHideSkeleton,
  isSendMoney = false,
  openingAt = null,
  onRelink,
  isACHSupported,
  isWireSupported,
  status = null
}) {
  const theme = useTheme();
  const { isWebView } = WebView;
  const { unitAccountId } = useUnitAccountId();
  const { businessEntityUuid } = useConstBusinessEntityUuid();
  const { redirectToLinkedAccounts } = useRedirectToLinkedAccounts(businessEntityUuid);
  const { createErrorToast } = useToastContext();
  const captureException = useCaptureException();
  // refetch counterpartyBalance to update the data
  const onCounterPartyReceived = () => {
    setShowSkeleton();
    resetPagination();
  };

  const onCounterPartyCreatonError = () => {
    setHideSkeleton();
  };

  const { isUpdatingPlaidLinkToken, updatePlaidLinkToken, isHandling } = usePlaid({
    unitAccountId,
    businessEntityUuid,
    onCounterPartyReceived,
    onCounterPartyCreatonError,
    isRelink: true,
    counterPartyId: id
  });

  const relinkHandler = useCallback(async () => {
    if (typeof onRelink === 'function') {
      onRelink();
      return;
    }

    if (redirectToLinkedAccounts({ useTopWindow: true })) {
      return;
    }

    if (isWebView) {
      const { pathname } = document.location;
      const pathName = openingAt ? `${pathname}?modal=${openingAt}` : pathname;
      try {
        relinkPlaidWithNativeApp({
          pathName,
          unitAccountId,
          counterpartyId: id
        });
      } catch (error) {
        captureException(error, {
          level: 'warning'
        });
      }
    } else {
      const { error } = await updatePlaidLinkToken();
      // handle show error
      if (error) {
        createErrorToast({
          title: retrievedGraphQLError(error),
          error
        });
        setHideSkeleton();
      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    id,
    openingAt,
    onRelink,
    unitAccountId,
    createErrorToast,
    updatePlaidLinkToken,
    redirectToLinkedAccounts
  ]);

  const isLoading = isUpdatingPlaidLinkToken || isHandling;
  const isNotRelinkable = isNotRelinkableStatus(status);
  const relinkActionLabel = getRelinkActionLabel(status);
  const pillText = isNotRelinkable ? "Can't be relinked" : 'No longer linked';

  const isPlaidType = connectedType === COUNTERPARTY_CONNECTED_TYPE.PLAID;
  const { subtitle, title } = useMemo(() => {
    if (isSendMoney && isPlaidType) {
      return {
        title: `${bankName} ${last4Digits}`,
        subtitle: `${capitalizeFirstLetter(accountType)}`
      };
    }
    return {
      title: `${bankName} ${last4Digits}`,
      subtitle: `${capitalizeFirstLetter(accountType)}`
    };
  }, [accountType, bankName, isPlaidType, isSendMoney, last4Digits]);

  const statuses = useMemo(() => {
    const list = [];
    if (isACHSupported) {
      list.push({
        status: 'ACH',
        color: 'primary',
        bgColor: theme.palette.background.grey.dark
      });
    }

    if (isWireSupported) {
      list.push({
        status: 'Wire',
        color: 'primary',
        bgColor: theme.palette.background.grey.dark
      });
    }

    return list;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isACHSupported, isWireSupported]);

  if (isSendMoney) {
    return (
      <AccountCommonV2
        buttonProps={{
          variant: 'outlined'
        }}
        buttonText={relinkActionLabel}
        disabled={isLoading}
        loading={isLoading}
        onClick={relinkHandler}
        pillText={pillText}
        statuses={statuses}
        subtitle={subtitle}
        title={title}
      />
    );
  }

  return (
    <AccountCommon
      disabled={isLoading}
      loading={isLoading}
      name={name}
      numberOfCharacters={numberOfCharacters}
      onClick={relinkHandler}
      statuses={[
        {
          status: pillText,
          color: 'primary',
          bgColor: theme.palette.background.red.light
        }
      ]}
      subtitle={subtitle}
      title={title}
    />
  );
}

RelinkAccount.propTypes = {
  id: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  bankName: PropTypes.string.isRequired,
  connectedType: PropTypes.string.isRequired,
  accountType: PropTypes.string.isRequired,
  last4Digits: PropTypes.string.isRequired,
  numberOfCharacters: PropTypes.number,
  resetPagination: PropTypes.func,
  setShowSkeleton: PropTypes.func,
  setHideSkeleton: PropTypes.func,
  isSendMoney: PropTypes.bool,
  openingAt: PropTypes.string,
  onRelink: PropTypes.func,
  isACHSupported: PropTypes.bool,
  isWireSupported: PropTypes.bool,
  status: PropTypes.string
};

export default memo(RelinkAccount);
